import React, { useEffect, useRef } from 'react';

/**
 * CardPreview — the real card thumbnail, recoloured to whatever the seller picked.
 *
 * The thumbnail is a render of the actual template, so remapping its flat colour
 * areas on a canvas gives an accurate preview without a server round-trip.
 */
const ROLES = [
  [[0x13, 0x13, 0x16], 'panel'],
  [[0x16, 0x16, 0x1a], 'panel'],
  [[0xeb, 0xeb, 0xec], 'paper'],
  [[0xf5, 0xf5, 0xf5], 'cell'],
  [[0xf2, 0xf3, 0xf4], 'cell'],
  [[0xe8, 0xea, 0xec], 'inkLight'],
  [[0xb9, 0xbe, 0xc3], 'metal'],
  [[0xa9, 0xae, 0xb3], 'metal'],
  [[0x7c, 0x10, 0x28], 'banner'],
];

function hex(h) {
  const v = String(h || '').replace('#', '');
  if (v.length !== 6) return null;
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
}
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));

// where the banner sits in the thumbnail, as fractions of width and height
const BANNER_BOX = { x0: 0.16, x1: 0.50, y0: 0.18, y1: 0.24 };

function targets(colours) {
  const P = hex(colours.paper) || [0xeb, 0xeb, 0xec];
  const D = hex(colours.dark) || [0x13, 0x13, 0x16];
  const G = hex(colours.gold) || [0xb9, 0xbe, 0xc3];
  const B = hex(colours.banner) || [0x7c, 0x10, 0x28];
  const white = [255, 255, 255];
  return { panel: D, paper: P, cell: mix(P, white, 0.55),
           inkLight: mix(G, white, 0.75), metal: G, banner: B };
}

export default function CardPreview({ style = 'black-silver', colours = {}, alt = 'Card preview', maxWidth = 520 }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      try {
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = data.data;
        const t = targets(colours);
        for (let i = 0; i < px.length; i += 4) {
          for (let r = 0; r < ROLES.length; r++) {
            const c = ROLES[r][0];
            if (Math.abs(px[i] - c[0]) + Math.abs(px[i + 1] - c[1]) + Math.abs(px[i + 2] - c[2]) < 30) {
              const n = t[ROLES[r][1]];
              px[i] = n[0]; px[i + 1] = n[1]; px[i + 2] = n[2];
              break;
            }
          }
        }

        // the banner label is white in the artwork; the card prints it black on a
        // light banner, so the preview has to do the same or the two disagree
        const bh = hex(colours.banner);
        if (bh) {
          const luma = (0.299 * bh[0] + 0.587 * bh[1] + 0.114 * bh[2]) / 255;
          if (luma > 0.6) {
            const x0 = Math.floor(BANNER_BOX.x0 * canvas.width);
            const x1 = Math.ceil(BANNER_BOX.x1 * canvas.width);
            const y0 = Math.floor(BANNER_BOX.y0 * canvas.height);
            const y1 = Math.ceil(BANNER_BOX.y1 * canvas.height);
            for (let y = y0; y < y1; y++) {
              for (let x = x0; x < x1; x++) {
                const i = (y * canvas.width + x) * 4;
                if (px[i] > 200 && px[i + 1] > 200 && px[i + 2] > 200) {
                  px[i] = 19; px[i + 1] = 19; px[i + 2] = 22;
                }
              }
            }
          }
        }

        ctx.putImageData(data, 0, 0);
      } catch (e) { /* canvas tainted: show the plain thumbnail */ }
    };
    img.src = `/card-styles/${style}.jpg`;
  }, [style, colours]);

  return <canvas ref={ref} aria-label={alt}
    style={{ width: '100%', maxWidth, height: 'auto', display: 'block', margin: '0 auto',
             borderRadius: 8, border: '1px solid var(--border-color, #E4DCCE)' }} />;
}
